<template>
  <div>
    <v-alert
      v-if="outOfPageViews()"
      type="warning"
      :class="mobile ? '' : 'text-center'">
      You've reached your maximum allotted page views. Please create a FREE account to continue.
    </v-alert>

    <p>
      We’re glad you’re so interested in our forecast model. We just need you to login or create 
      a FREE Decision Desk HQ account to continue. Once you do, you ‘ll have access to all of our 
      House and Senate race pages.
    </p>

    <div class="login-container pt-4">
      <div class="social-login-buttons">
        <div class="signin-button facebook-button" @click="facebook">
          <span class="icon icon-facebook"></span>
          <span>Sign In With Facebook</span>
        </div>
        <div class="signin-button google-button" @click="google">
          <span class="icon icon-google"></span>
          <span>Sign In With Google</span>
        </div>
        <div class="signin-button linkedin-button" @click="linkedin">
          <span class="icon icon-linkedin"></span>
          <span>Sign In With LinkedIn</span>
        </div>

        <p style="text-align: center; font-size: 14px; margin-top: 8px;">Single Sign-On Provided by <a href="https://auth0.com/" target="_blank">Auth0</a></p>

        <div class="separator">
          <span>or</span>
        </div>
      </div>

      <v-form class="login-form" ref="form" style="max-width: 400px" v-model="valid" :key="formType">
        <h3 class="normal" style="font-size: 18px;"
          v-if="formType === 'login'">Login With Email:</h3>
        <h3 class="normal" style="font-size: 18px;"
          v-if="formType === 'signup'">Signup With Email:</h3>

        <div class="login-actions">
          <span v-if="!$route.params.email && formType === 'login'">
            <a href="#" @click="setFormType($event, 'reset')">Forgot password?</a>
          </span>
          <span v-else>
            <a href="#" @click="setFormType($event, 'login')">Go to Login</a>
          </span>
          <span v-if="formType === 'login'">
            <a href="#" @click="setFormType($event, 'signup')">Create a FREE Account</a>
          </span>
        </div>

        <p v-if="$route.params.email && $route.params.reset === true">A password reset link has been sent to {{ $route.params.email }}:</p>
        <p v-if="$route.params.email">Thanks for signing up! Please sign in below:</p>

        <template v-if="errors.nonFieldErrors">
          <p class="red--text" v-for="(error, index) in errors.nonFieldErrors" :key="index"
            >{{ error }}</p>
        </template>
        <template v-if="formType === 'signup'">
          <!-- First Name -->
          <v-text-field required
            :error-messages="errors.firstName"
            :rules="rules.firstName"
            :validate-on-blur="true"
            v-model="firstName"
            @keypress.enter.native="formAction"
            label="First Name">
          </v-text-field>

          <!-- Last Name -->
          <v-text-field required
            :error-messages="errors.lastName"
            :rules="rules.lastName"
            :validate-on-blur="true"
            v-model="lastName"
            @keypress.enter.native="formAction"
            label="Last Name">
          </v-text-field>

          <!-- Postal Code -->
          <v-text-field required
            :error-messages="errors.postalCode"
            :rules="rules.postalCode"
            :validate-on-blur="true"
            v-model="postalCode"
            @keypress.enter.native="formAction"
            label="Postal Code"
            max="5"
            @keypress="postalCodeKeyPress">
          </v-text-field>

          <v-select required
            :error-messages="errors.country"
            :rules="rules.country"
            :validate-on-blur="true"
            @keypress.enter.native="formAction"
            v-model="country"
            :items="countryList"
            label="Country"
          ></v-select>
        </template>

        <!-- Email -->
        <v-text-field required
          :error-messages="errors.email"
          :rules="rules.email"
          :validate-on-blur="true"
          v-model="email"
          @keypress.enter.native="formAction"
          label="Email"
          type="email">
        </v-text-field>

        <!-- Password -->
        <v-text-field v-if="formType !== 'reset'" required
          :error-messages="errors.password"
          :rules="rules.password"
          :validate-on-blur="formType === 'signup'"
          v-model="password"
          @keypress.enter.native="formAction"
          label="Password"
          type="password">
        </v-text-field>

        <template v-if="formType === 'signup'">
          <!-- Password 2 -->
          <v-text-field required
            :error-messages="errors.password2"
            :rules="rules.password2"
            :validate-on-blur="true"
            v-model="password2"
            @keypress.enter.native="formAction"
            label="Confirm Password"
            type="password">
          </v-text-field>
        </template>

        <!-- Button -->
        <v-btn ref="actionbutton" v-if="formType === 'signup'" style="margin-left: 0"
          color="primary"
          depressed tile
          :loading="loading"
          @click="signup"
          >Sign Up</v-btn>
        <v-btn ref="actionbutton" v-else-if="formType === 'login'" style="margin-left: 0"
          color="primary"
          depressed tile
          :loading="loading"
          @click="login"
          >Log In</v-btn>
        <v-btn ref="actionbutton" v-else-if="formType === 'reset'" style="margin-left: 0"
          color="primary"
          depressed tile
          :loading="loading"
          @click="reset"
          >Reset Password</v-btn>
      </v-form>
    </div>
  </div>
</template>

<script>
import { getRemainingPageViews } from "@/mixins/cookies";
import { mapGetters } from "vuex";

const name = 'login'

const computed = {
  ...mapGetters({
    mobile: "ux/mobile",
  }),
};

const methods = {
  outOfPageViews() {
    if (this.$auth.authenticated) return false;

    const remainingViews = getRemainingPageViews();
    return remainingViews === 0;
  },
  login () {
    if (this.$refs.form.validate()) {
      delete this.$route.params.email
      this.errors = {}
      this.loading = true
      this.$forceUpdate()
      this.$auth.webAuth.client.login({
        realm: 'Email-Password-Authorization',
        email: this.email,
        username: this.email,
        password: this.password
      }, (error, authResult) => {
        if (error) {
          this.errors.nonFieldErrors = [error.description]
          this.$forceUpdate()
        } else {
          this.$auth.login(authResult.accessToken, this.email)
          this.$emit('login', this.email)
          if (this.$route.name === 'login') {
            this.$router.push('/')
          }
        }
        setTimeout(() => {
          this.loading = false
        }, 400)
      })
    }
  },
  signup () {
    if (this.$refs.form.validate()) {
      this.errors = {}
      this.loading = true
      this.$auth.webAuth.signup({
        connection: 'Email-Password-Authorization',
        email: this.email,
        password: this.password,
        user_metadata: {
          firstName: this.firstName,
          lastName: this.lastName,
          postalCode: this.postalCode,
          country: this.country
        }
      }, (error) => {
        if (error) {
          if (typeof error.description === 'string') {
            this.errors.nonFieldErrors = [error.description]
          } else if (error.name === 'PasswordStrengthError') {
            // this.errors.nonFieldErrors = ['Password is too weak']
            this.errors.password = 'Password is too weak. Hint: To make it stronger, use upper and lower case letters, numbers and symbols like ! " ? $ % ^ & ).'
          } else {
            this.errors.nonFieldErrors = ['There was an error creating your account.']
          }
          this.loading = false
          this.$forceUpdate()
        } else {
          // this.$router.replace({ name: 'login', params: { email: this.email } })
          // this.firstName = ''
          // this.lastName = ''
          // this.postalCode = ''
          // this.country = ''
          // this.password = ''
          // this.password2 = ''
          // this.formType = 'login'
          // this.$route.params.email = this.email
          this.login()
        }
        // this.loading = false
      })
    }
  },
  reset () {
    if (this.$refs.form.validate()) {
      this.errors = {}
      this.loading = true
      this.$auth.webAuth.changePassword({
        connection: 'Email-Password-Authorization',
        email: this.email
      }, (error) => {
        if (error) {
          this.errors.nonFieldErrors = [error]
        } else {
          this.email = ''
          this.formType = 'login'
          this.$route.params.email = this.email
          this.$route.params.reset = true
        }
        this.loading = false
      })
    }
  },
  postalCodeKeyPress (e) {
    if (e.key.match(/[a-z0-9]/i) === null) {
      e.preventDefault()
    }
  },
  formAction (e) {
    e.preventDefault()
    this.errors = {}
    delete this.$route.params.email
    delete this.$route.params.reset
    if (this.formType === 'signup') {
      this.signup()
    } else if (this.formType === 'login') {
      this.login()
    } else if (this.formType === 'reset') {
      this.reset()
    }
  },
  setFormType (e, type) {
    e.preventDefault()
    this.errors = {}
    this.formType = type
  },
  facebook () {
    this.$auth.webAuth.authorize({
      connection: 'facebook',
      responseType: 'token',
      redirectUri: `${window.location.origin}?redirect=${window.location.href}`
    })
  },
  google () {
    this.$auth.webAuth.authorize({
      connection: 'google-oauth2',
      responseType: 'token',
      redirectUri: `${window.location.origin}?redirect=${window.location.href}`
    })
  },
  linkedin () {
    this.$auth.webAuth.authorize({
      connection: 'linkedin',
      responseType: 'token',
      redirectUri: `${window.location.origin}?redirect=${window.location.href}`
    })
  }
}

/* eslint-disable-next-line */
const COUNTRY_LIST = {"United States": "us", "Afghanistan": "af", "Åland Islands": "ax", "Albania": "al", "Algeria": "dz", "American Samoa": "as", "Andorra": "ad", "Angola": "ao", "Anguilla": "ai", "Antarctica": "aq", "Antigua and Barbuda": "ag", "Argentina": "ar", "Armenia": "am", "Aruba": "aw", "Australia": "au", "Austria": "at", "Azerbaijan": "az", "Bahamas": "bs", "Bahrain": "bh", "Bangladesh": "bd", "Barbados": "bb", "Belarus": "by", "Belgium": "be", "Belize": "bz", "Benin": "bj", "Bermuda": "bm", "Bhutan": "bt", "Bolivia, Plurinational State of": "bo", "Bonaire, Sint Eustatius and Saba": "bq", "Bosnia and Herzegovina": "ba", "Botswana": "bw", "Bouvet Island": "bv", "Brazil": "br", "British Indian Ocean Territory": "io", "Brunei Darussalam": "bn", "Bulgaria": "bg", "Burkina Faso": "bf", "Burundi": "bi", "Cambodia": "kh", "Cameroon": "cm", "Canada": "ca", "Cape Verde": "cv", "Cayman Islands": "ky", "Central African Republic": "cf", "Chad": "td", "Chile": "cl", "China": "cn", "Christmas Island": "cx", "Cocos (Keeling) Islands": "cc", "Colombia": "co", "Comoros": "km", "Congo": "cg", "Congo, the Democratic Republic of the": "cd", "Cook Islands": "ck", "Costa Rica": "cr", "Côte d'Ivoire": "ci", "Croatia": "hr", "Cuba": "cu", "Curaçao": "cw", "Cyprus": "cy", "Czech Republic": "cz", "Denmark": "dk", "Djibouti": "dj", "Dominica": "dm", "Dominican Republic": "do", "Ecuador": "ec", "Egypt": "eg", "El Salvador": "sv", "Equatorial Guinea": "gq", "Eritrea": "er", "Estonia": "ee", "Ethiopia": "et", "Falkland Islands (Malvinas)": "fk", "Faroe Islands": "fo", "Fiji": "fj", "Finland": "fi", "France": "fr", "French Guiana": "gf", "French Polynesia": "pf", "French Southern Territories": "tf", "Gabon": "ga", "Gambia": "gm", "Georgia": "ge", "Germany": "de", "Ghana": "gh", "Gibraltar": "gi", "Greece": "gr", "Greenland": "gl", "Grenada": "gd", "Guadeloupe": "gp", "Guam": "gu", "Guatemala": "gt", "Guernsey": "gg", "Guinea": "gn", "Guinea-Bissau": "gw", "Guyana": "gy", "Haiti": "ht", "Heard Island and McDonald Islands": "hm", "Holy See (Vatican City State)": "va", "Honduras": "hn", "Hong Kong": "hk", "Hungary": "hu", "Iceland": "is", "India": "in", "Indonesia": "id", "Iran, Islamic Republic of": "ir", "Iraq": "iq", "Ireland": "ie", "Isle of Man": "im", "Israel": "il", "Italy": "it", "Jamaica": "jm", "Japan": "jp", "Jersey": "je", "Jordan": "jo", "Kazakhstan": "kz", "Kenya": "ke", "Kiribati": "ki", "Korea, Democratic People's Republic of": "kp", "Korea, Republic of": "kr", "Kuwait": "kw", "Kyrgyzstan": "kg", "Lao People's Democratic Republic": "la", "Latvia": "lv", "Lebanon": "lb", "Lesotho": "ls", "Liberia": "lr", "Libya": "ly", "Liechtenstein": "li", "Lithuania": "lt", "Luxembourg": "lu", "Macao": "mo", "Macedonia, the Former Yugoslav Republic of": "mk", "Madagascar": "mg", "Malawi": "mw", "Malaysia": "my", "Maldives": "mv", "Mali": "ml", "Malta": "mt", "Marshall Islands": "mh", "Martinique": "mq", "Mauritania": "mr", "Mauritius": "mu", "Mayotte": "yt", "Mexico": "mx", "Micronesia, Federated States of": "fm", "Moldova, Republic of": "md", "Monaco": "mc", "Mongolia": "mn", "Montenegro": "me", "Montserrat": "ms", "Morocco": "ma", "Mozambique": "mz", "Myanmar": "mm", "Namibia": "na", "Nauru": "nr", "Nepal": "np", "Netherlands": "nl", "New Caledonia": "nc", "New Zealand": "nz", "Nicaragua": "ni", "Niger": "ne", "Nigeria": "ng", "Niue": "nu", "Norfolk Island": "nf", "Northern Mariana Islands": "mp", "Norway": "no", "Oman": "om", "Pakistan": "pk", "Palau": "pw", "Palestine, State of": "ps", "Panama": "pa", "Papua New Guinea": "pg", "Paraguay": "py", "Peru": "pe", "Philippines": "ph", "Pitcairn": "pn", "Poland": "pl", "Portugal": "pt", "Puerto Rico": "pr", "Qatar": "qa", "Réunion": "re", "Romania": "ro", "Russian Federation": "ru", "Rwanda": "rw", "Saint Barthélemy": "bl", "Saint Helena, Ascension and Tristan da Cunha": "sh", "Saint Kitts and Nevis": "kn", "Saint Lucia": "lc", "Saint Martin (French part)": "mf", "Saint Pierre and Miquelon": "pm", "Saint Vincent and the Grenadines": "vc", "Samoa": "ws", "San Marino": "sm", "Sao Tome and Principe": "st", "Saudi Arabia": "sa", "Senegal": "sn", "Serbia": "rs", "Seychelles": "sc", "Sierra Leone": "sl", "Singapore": "sg", "Sint Maarten (Dutch part)": "sx", "Slovakia": "sk", "Slovenia": "si", "Solomon Islands": "sb", "Somalia": "so", "South Africa": "za", "South Georgia and the South Sandwich Islands": "gs", "South Sudan": "ss", "Spain": "es", "Sri Lanka": "lk", "Sudan": "sd", "Suriname": "sr", "Svalbard and Jan Mayen": "sj", "Swaziland": "sz", "Sweden": "se", "Switzerland": "ch", "Syrian Arab Republic": "sy", "Taiwan, Province of China": "tw", "Tajikistan": "tj", "Tanzania, United Republic of": "tz", "Thailand": "th", "Timor-Leste": "tl", "Togo": "tg", "Tokelau": "tk", "Tonga": "to", "Trinidad and Tobago": "tt", "Tunisia": "tn", "Turkey": "tr", "Turkmenistan": "tm", "Turks and Caicos Islands": "tc", "Tuvalu": "tv", "Uganda": "ug", "Ukraine": "ua", "United Arab Emirates": "ae", "United Kingdom": "gb", "United States Minor Outlying Islands": "um", "Uruguay": "uy", "Uzbekistan": "uz", "Vanuatu": "vu", "Venezuela, Bolivarian Republic of": "ve", "Viet Nam": "vn", "Virgin Islands, British": "vg", "Virgin Islands, U.S.": "vi", "Wallis and Futuna": "wf", "Western Sahara": "eh", "Yemen": "ye", "Zambia": "zm", "Zimbabwe": "zw"}

export default {
  name,
  computed,
  methods,
  data () {
    const countryList = Object.keys(COUNTRY_LIST).map(key => ({
      text: key,
      value: COUNTRY_LIST[key]
    }))
    return {
      formType: 'login',
      loading: false,
      valid: false,
      countryList,
      firstName: '',
      lastName: '',
      email: '',
      postalCode: '',
      country: '',
      password: '',
      password2: '',
      errors: {},
      rules: {
        firstName: [
          v => !!v || 'First Name is required',
          v => v.length >= 2 || 'First Name should be more then one character long'
        ],
        lastName: [
          v => !!v || 'Last Name is required',
          v => v.length >= 2 || 'Last Name should be more then one character long'
        ],
        postalCode: [
          v => !!v || 'Postal Code is required'
        ],
        country: [
          v => !!v || 'Country is required'
        ],
        email: [
          v => !!v || 'E-mail is required',
          /* eslint-disable-next-line */
          v => /^\w+([\+.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'E-mail must be valid'
        ],
        password: [
          v => !!v || 'Password is required'
        ],
        password2: [
          v => v === this.password || 'Passwords must match'
        ]
      }
    }
  },
  beforeMount () {
    if (this.$route.params.email) {
      this.email = this.$route.params.email
    }
  }
}
</script>

<style>

.login-container {
  display: flex;
  justify-content: center;
  margin: 10px;
}

@media only screen and (max-width: 767px) {
  .login-container {
    flex-direction: column;
    max-width: 30rem;
  }
}

@media only screen and (max-width: 480px) {
  .login-container {
    max-width: 100%;
  }
}

.social-login-buttons {
  display: flex;
  flex-direction: column;
  position: relative;
}

.signin-button {
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  width: 240px;
  color: #ffffff;
}

.signin-button:hover {
  opacity: 0.9;
}

.signin-button .icon {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 22px;
  margin-right: 5px;
}

.social-login-buttons .separator span {
  width: 4rem;
  height: 4rem;
  line-height: 3.5rem;
  position: absolute;
  background-color: var(--secondary-color);
  border: 2px solid var(--border-color);
  border-radius: 50%;
  text-align: center;
  font-weight: 700;
}

@media only screen and (min-width: 768px) {
  .social-login-buttons {
    /* flex-direction: column; */
    margin-right: 4.5rem;
    padding-right: 4rem;
  }

  .social-login-buttons .separator {
    border-right: 2px solid var(--border-color);
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
  }

  .social-login-buttons .separator span {
    top: 50%;
    right: 0;
    margin-top: -2rem;
    margin-right: -2rem;
  }
}

@media only screen and (max-width: 767px) {
  .social-login-buttons .separator {
    border-bottom: 2px solid var(--border-color);
    position: relative;
    margin-top: 4rem;
    margin-bottom: 4rem;
  }

  .social-login-buttons .separator span {
    left: 50%;
    top: 0;
    margin-top: -2rem;
    margin-left: -2rem;
  }
}

@media only screen and (max-width: 420px) {
  .signin-button {
    width: 100%;
  }
}

.social-login-buttons .facebook-button {
  background-color: #4267b2!important;
}

.social-login-buttons .google-button {
  background-color: #ea4336!important;
}

.social-login-buttons .linkedin-button {
  background-color: #0276b5!important;
}

.login-actions {
  margin-top: 1rem;
  font-size: 14px;
}

.login-actions span {
  line-height: 1;
}

.login-actions span + span {
  border-left: 1px solid var(--border-color);
  margin-left: 0.5rem;
  padding-left: 0.75rem;
}
</style>
